import { Dialog } from "primereact/dialog";
import { SafeReportResponse } from "../Conversion";
import { useMemo, useState } from "react";
import { useToast } from "../../../context-providers/Toast";
import { Button } from "primereact/button";
import { generatePolicyFlyApplication } from "../../../api/routes";
import { useCalculatedPremium, useThisReportIsUpdating } from "../data";
import styles from "./PolicyFlyCard.module.css";
import {
  getEffectiveIncludedPerils,
  perilSentenceFromEnabledPerils,
} from "../general-input/util";
import {
  readableLayer,
  readableLimitOfLiabilityFromLayer,
  sortedLayersFromReport,
} from "../layers/util";
import * as client from "../../../client";
import { Message } from "primereact/message";
import { formatExactMoneyDollars, LayerPremiumSummary } from "../Money";

type PolicyFlyModalProps = {
  specialtyPropertyInfo: SafeReportResponse;
  visible: boolean;
  setVisible: (value: boolean) => void;
};

export const PolicyFlyModal: React.FC<PolicyFlyModalProps> = ({
  specialtyPropertyInfo,
  visible,
  setVisible,
}) => {
  const [taskSubmitted, setTaskSubmitted] = useState(false);
  const toast = useToast();
  const companyInfo = specialtyPropertyInfo.report_json.company_info;
  const allPerils =
    specialtyPropertyInfo.report_json.policy_info?.included_perils ?? [];
  const perilSentenceOverride =
    specialtyPropertyInfo.report_json.policy_info?.peril_sentence_override;
  const allLayers = sortedLayersFromReport(specialtyPropertyInfo);

  const premium = useCalculatedPremium(
    specialtyPropertyInfo.report_id,
    visible
  );

  // Build per-layer peril sentences for the API call and display
  const perilsByLayer = useMemo(() => {
    const result: Record<string, string> = {};
    for (const layer of allLayers) {
      if (!layer.is_enabled || !layer.layer_id) continue;
      const effective = getEffectiveIncludedPerils(
        allPerils,
        premium.data,
        layer.layer_id
      );
      result[layer.layer_id] = perilSentenceFromEnabledPerils(
        effective,
        perilSentenceOverride
      );
    }
    return result;
  }, [allLayers, allPerils, perilSentenceOverride, premium]);

  // Build a lookup from layer_id to premium totals for the selected source
  const premiumByLayer = useMemo(() => {
    const result: Record<string, client.LayerTotals> = {};
    if (!premium.data) return result;
    for (const layerPremium of premium.data.layers) {
      const layer = allLayers.find((l) => l.layer_id === layerPremium.layer_id);
      if (!layer) continue;
      const totals = layerPremium.totals["resolved"];
      if (totals) {
        result[layerPremium.layer_id] = totals;
      }
    }
    return result;
  }, [premium, allLayers]);
  const enabledLayers = allLayers.filter((layer) => layer.is_enabled);

  const policyFlyInfo = specialtyPropertyInfo.report_json.policy_fly_info;
  const applications = policyFlyInfo?.applications ?? [];

  // Build a lookup from layer_id to its applications. A layer can have more
  // than one application, so collect them into a list.
  const appsByLayerId = useMemo(() => {
    const map = new Map<string, client.PolicyFlyApplication[]>();
    for (const app of applications) {
      const list = map.get(app.layer_id) ?? [];
      list.push(app);
      map.set(app.layer_id, list);
    }
    return map;
  }, [applications]);

  const completedApplications = applications.filter((app) => !!app.url);
  const layersWithoutApps = enabledLayers.filter((layer) => {
    const apps = appsByLayerId.get(layer.layer_id ?? "") ?? [];
    return !apps.some((app) => !!app.url);
  });
  const allLayersHaveUrls =
    enabledLayers.length > 0 && layersWithoutApps.length === 0;

  // Check if a Policy Fly task is already running via the generalized polling hook
  const isPolicyFlyRunning = useThisReportIsUpdating(
    specialtyPropertyInfo.report_id,
    "POLICY_FLY"
  );

  const isTaskActive = !!isPolicyFlyRunning || taskSubmitted;

  const handleCreate = async (additionalLayerId?: string) => {
    setTaskSubmitted(true);
    try {
      await generatePolicyFlyApplication(
        specialtyPropertyInfo.report_id,
        perilsByLayer,
        additionalLayerId
      );
    } catch {
      toast.show({
        severity: "error",
        summary: "Failed to Generate Policy Fly Application",
        detail: "Could not start the Policy Fly application request.",
        sticky: true,
      });
    } finally {
      setTaskSubmitted(false);
    }
  };

  const layerForApp = (
    app: client.PolicyFlyApplication
  ): client.Layer | undefined => {
    return allLayers.find((l) => l.layer_id === app.layer_id);
  };

  return (
    <Dialog
      header="Policy Fly Application"
      visible={visible}
      style={{
        width: "calc(100vw - 72px)",
        height: "calc(100vh - 72px)",
      }}
      onHide={() => setVisible(false)}
      footer={
        <div>
          <Button
            label={`Create ${layersWithoutApps.length} Policy Fly application${layersWithoutApps.length === 1 ? "" : "s"}`}
            loading={isTaskActive}
            onClick={() => handleCreate()}
            disabled={
              allLayersHaveUrls || enabledLayers.length === 0 || !premium.data
            }
          />
        </div>
      }
    >
      <div className={styles.verticalContainer}>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Named insured</span>
          <span>{companyInfo.company_name}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Mailing address</span>
          <span>{`${companyInfo.company_address}, ${companyInfo.company_city}, ${companyInfo.company_state}, ${companyInfo.company_zip}`}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Effective date</span>
          <span>{companyInfo.effective_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Expiration date</span>
          <span>{companyInfo.expiration_date}</span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Total insured value</span>
          <span>
            {formatExactMoneyDollars(
              specialtyPropertyInfo.precomputed_metadata.total_tiv
            )}
          </span>
        </div>
        <div className={styles.verticalContainerSmall}>
          <span className={styles.header}>Layers</span>
          {enabledLayers.length === 0 && (
            <div>
              <Message severity="warn" text="Need at least 1 enabled layer" />
            </div>
          )}
          <div className={styles.layerList}>
            {enabledLayers.map((layer) => {
              const totals = premiumByLayer[layer.layer_id ?? ""];
              const layerApps = appsByLayerId.get(layer.layer_id ?? "") ?? [];
              const completedCount = layerApps.filter(
                (app) => !!app.url
              ).length;
              return (
                <div key={layer.layer_id} className={styles.layerRow}>
                  <span>{readableLimitOfLiabilityFromLayer(layer)}</span>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      color: "var(--text-color-secondary)",
                    }}
                  >
                    {perilsByLayer[layer.layer_id ?? ""] ?? ""}
                  </div>
                  {totals && (
                    <LayerPremiumSummary
                      layerTotal={totals.layer_total}
                      shareTotal={totals.share_total}
                    />
                  )}
                  {completedCount > 0 && layer.layer_id && (
                    <Button
                      label={`Create another application for this layer (${completedCount} so far)`}
                      icon="pi pi-plus"
                      outlined
                      severity="secondary"
                      size="small"
                      loading={isTaskActive}
                      disabled={isTaskActive || !premium.data}
                      onClick={() => handleCreate(layer.layer_id ?? undefined)}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
        {completedApplications.length > 0 && (
          <div className={styles.verticalContainerSmall}>
            <span className={styles.header}>Policy Fly Applications</span>
            {completedApplications.map((app) => {
              const layer = layerForApp(app);
              const label = layer
                ? `${readableLayer(layer)} - ${app.suffix}`
                : `Layer ${app.suffix}`;
              return (
                <div key={app.suffix}>
                  <Button
                    label={label}
                    icon="pi pi-external-link"
                    outlined
                    onClick={() => {
                      window.open(app.url!, "_blank", "noopener,noreferrer");
                    }}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </Dialog>
  );
};
